import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { AdministrationUserService } from 'src/app/core/services/administration/user.service';
import * as UsersActions from './users.actions';
import { Store } from '@ngrx/store';
import { ToastActions } from '../../toast/toast.actions';

@Injectable()
export class AdministrationUsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(AdministrationUserService);
  private readonly store = inject(Store);

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      switchMap(({ params }) =>
        this.userService.getAllUsers(params).pipe(
          map((response) =>
            UsersActions.loadUsersSuccess({
              users: response.data ?? [],
              page: response.page,
            })
          ),
          catchError((error) =>
            of(
              UsersActions.loadUsersFailure({
                error: error?.message || 'Failed to load users',
              })
            )
          )
        )
      )
    )
  );

  loadUserById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUserById),
      switchMap(({ id }) =>
        this.userService.getUserById({ id }).pipe(
          map((response) => {
            const user = response.data?.[0];
            if (!user) {
              throw new Error('User not found');
            }
            return UsersActions.loadUserByIdSuccess({ user });
          }),
          catchError((error) =>
            of(
              UsersActions.loadUserByIdFailure({
                error: error?.message || 'Failed to load user details',
              })
            )
          )
        )
      )
    )
  );

  updateUserStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateUserStatus),
      switchMap(({ id, status }) =>
        this.userService.updateUserStatus({ id, body: { status } }).pipe(
          map((response) => {
            const user = response.data?.[0];
            if (!user) {
              throw new Error('Failed to update user status');
            }
            return UsersActions.updateUserStatusSuccess({ user });
          }),
          catchError((error) =>
            of(
              UsersActions.updateUserStatusFailure({
                error: error?.message || 'Failed to update user status',
              })
            )
          )
        )
      )
    )
  );

  updateUserStatusSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.updateUserStatusSuccess),
        tap(() => {
          this.store.dispatch(
            ToastActions.showSuccessToast({
              summary: 'Success',
              detail: 'User status updated successfully',
            })
          );
        })
      ),
    { dispatch: false }
  );

  updateUserStatusFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersActions.updateUserStatusFailure),
        tap(({ error }) => {
          this.store.dispatch(
            ToastActions.showErrorToast({
              summary: 'Error',
              detail: error || 'Failed to update user status',
            })
          );
        })
      ),
    { dispatch: false }
  );
}
